/** Small helpers used across the app. */

export function pickLocale(obj, lang, fallback = "it") {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj[fallback] || obj.it || obj.en || obj.de || Object.values(obj)[0] || "";
}

export function mapsUrl({ lat, lng, address }) {
  if (lat && lng) return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  if (address) return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
  return null;
}

/**
 * Build a Google Maps URL that lands on the actual property.
 * Priority:
 *   1. `settings.property_maps_url` — canonical link set by the owner (best UX,
 *       shows the business card panel next to the marker).
 *   2. Exact coordinates → coordinate search (marker at correct point, no panel).
 *   3. Business name + address → geocoded search (least accurate, avoid).
 */
export function propertyMapsUrl(settings) {
  if (!settings) return null;
  if (settings.property_maps_url) return settings.property_maps_url;
  const { property_lat: lat, property_lng: lng } = settings;
  if (lat && lng) {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }
  if (settings.contact_address) {
    const q = [settings.site_name, settings.contact_address].filter(Boolean).join(", ");
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  }
  return null;
}

export function telHref(number) {
  if (!number) return null;
  return `tel:${String(number).replace(/[^+0-9]/g, "")}`;
}

export function mailHref(email) {
  return email ? `mailto:${email}` : null;
}

/** Build a wa.me link from a phone number, stripping symbols/spaces. */
export function whatsappHref(number) {
  if (!number) return null;
  const digits = String(number).replace(/[^0-9]/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits}`;
}

/** Extract a YouTube video ID from a URL, short URL, embed URL, or bare ID. */
export function extractYouTubeId(input) {
  if (!input) return null;
  const s = String(input).trim();
  // Already an ID (11 chars, alnum + - _)
  if (/^[A-Za-z0-9_-]{11}$/.test(s)) return s;
  try {
    const u = new URL(s);
    if (u.hostname.includes("youtu.be")) {
      return u.pathname.replace(/^\//, "").split("/")[0] || null;
    }
    if (u.hostname.includes("youtube.com")) {
      const v = u.searchParams.get("v");
      if (v) return v;
      // Embed / shorts / live
      const m = u.pathname.match(/(?:embed|shorts|live|v)\/([A-Za-z0-9_-]{11})/);
      if (m) return m[1];
    }
  } catch { /* not a URL */ }
  return null;
}

/** Extract a "@handle" from a social profile URL. Best-effort. */
export function socialHandle(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const segments = u.pathname.split("/").filter(Boolean);
    if (!segments.length) return null;
    // Ignore obvious system paths
    if (["p", "reel", "reels", "pages", "explore"].includes(segments[0].toLowerCase())) return null;
    return `@${segments[0]}`;
  } catch {
    return null;
  }
}

/** WMO weather code → simple label. */
export function weatherLabel(code, lang = "it") {
  const map = {
    0: { it: "Sereno", en: "Clear", de: "Klar" },
    1: { it: "Poco nuvoloso", en: "Mostly clear", de: "Leicht bewölkt" },
    2: { it: "Parzialmente nuvoloso", en: "Partly cloudy", de: "Teilweise bewölkt" },
    3: { it: "Nuvoloso", en: "Overcast", de: "Bedeckt" },
    45: { it: "Nebbia", en: "Fog", de: "Nebel" },
    48: { it: "Nebbia gelata", en: "Rime fog", de: "Raureif-Nebel" },
    51: { it: "Pioviggine", en: "Drizzle", de: "Nieselregen" },
    61: { it: "Pioggia leggera", en: "Light rain", de: "Leichter Regen" },
    63: { it: "Pioggia", en: "Rain", de: "Regen" },
    65: { it: "Pioggia intensa", en: "Heavy rain", de: "Starker Regen" },
    71: { it: "Neve leggera", en: "Light snow", de: "Leichter Schnee" },
    80: { it: "Rovesci", en: "Showers", de: "Schauer" },
    95: { it: "Temporale", en: "Thunderstorm", de: "Gewitter" },
  };
  return (map[code] || { it: "—", en: "—", de: "—" })[lang] || "—";
}

export function weatherIconName(code) {
  if (code === 0) return "Sun";
  if ([1, 2].includes(code)) return "CloudSun";
  if (code === 3) return "Cloud";
  if ([45, 48].includes(code)) return "CloudFog";
  if ([51, 61, 63, 65, 80].includes(code)) return "CloudRain";
  if ([71, 73, 75].includes(code)) return "CloudSnow";
  if ([95, 96, 99].includes(code)) return "CloudLightning";
  return "Cloud";
}
